import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Grid, GridCustomCellProps, GridColumn as Column, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from "@progress/kendo-react-indicators";
import { Checkbox, NumericTextBox, NumericTextBoxChangeEvent, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { JsonResponse } from 'TypeGen/json-response';
import CenterDivPanel from '../../components/CenterDivPanel';
import SimpleDatePicker from '../../components/SimpleDatePicker';
import useAlert from '../../components/useAlert';
import { RightAlignHeaderCell } from '../../components/cells/RightAlignHeaderCell';
import { fetchApi } from '../../services/api';
import { isNullOrWhiteSpace } from '../../utils/utils';
import { PayByLinehaulType } from 'TypeGen/AccountsPayable/RateMaintenance/Linehaul/pay-by-linehaul-type';
import { formatNumber } from '@progress/kendo-intl';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { IDName } from 'types/idname';

type Props = {
  CloseDialog?: () => void;
};

export type PayRateSummary = {
  PayRates: PayRateViewModel[];
};

export type PayRateViewModel = {
  PayRateLinehaulID: number;

  PayBy: PayByLinehaulType;
  PayRate: number;

  ModifiedByFullName: string;
  ModifiedDateTime: Date;
  StartDate: Date;
  EndDate: Date;
  Name: string;

  inEdit?: boolean | string;
  isNew?: boolean;
  IsExpired: boolean;
};

const PayByLinehaulTypeList: IDName[] = [
  { ID: PayByLinehaulType.PerMile, Name: "Per Mile" },
  { ID: PayByLinehaulType.PercOfLH, Name: "% of LH" },
  { ID: PayByLinehaulType.Flat, Name: "Flat" },
  { ID: PayByLinehaulType.PerKM, Name: "Per KM" },
  { ID: PayByLinehaulType.Tiered, Name: "Tiered" },
  { ID: PayByLinehaulType.Hourly, Name: "Hourly" },
  { ID: PayByLinehaulType.PercOfTotalRev, Name: "% of Total Rev" },
];

const LastModifiedByCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: PayRateViewModel = props.dataItem;
  return (
    <td {...props.tdProps}>
      <span>{dayjs.utc(dataItem.ModifiedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")} - {dataItem.ModifiedByFullName}</span>
    </td>
  );
};

const textBoxCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const handleChange = (e: TextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  const value = props.dataItem[props.field] as string;
  const dataItem: PayRateViewModel = props.dataItem;

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ?
        <TextBox
          value={value}
          maxLength={30}
          onChange={handleChange}
          style={{ width: '100%' }}
        /> :
        <span>{value}</span>
      }
    </td>
  );
};

interface EditCommandCellProps extends GridCustomCellProps {
  edit: (item: PayRateViewModel) => void;
  //remove: (item: PayRateViewModel) => void;
  add: (item: PayRateViewModel) => void;
  discard: () => void;
  update: (item: PayRateViewModel) => void;
  cancel: (item: PayRateViewModel) => void;
  editField: string;
}

const RateCell = (props: GridCustomCellProps) => {
  const handleChange = (e: NumericTextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.value
      });
    }
  };

  const dataItem: PayRateViewModel = props.dataItem;
  const value = props.dataItem[props.field] as number;
  const rateFormat = dataItem.PayBy === PayByLinehaulType.PercOfLH || dataItem.PayBy === PayByLinehaulType.PercOfTotalRev ? "p0" : "c2";

  return (
    <td {...props.tdProps} style={{ textAlign: "right" }}>
      {dataItem.inEdit ? <NumericTextBox format={rateFormat} step={0.01} value={value} onChange={handleChange} min={0} inputStyle={{ textAlign: "right" }} /> :
        <span>{formatNumber(value, rateFormat)}</span>}
    </td>
  );
};

const PayByCell = (props: GridCustomCellProps) => {

  const dataItem: PayRateViewModel = props.dataItem;

  const handleChange = (e: DropDownListChangeEvent) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.ID,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? <DropDownList
        className="my-1"
        data={PayByLinehaulTypeList}
        textField="Name"
        dataItemKey="ID"
        value={PayByLinehaulTypeList.find((x: IDName) => x.ID == dataItem.PayBy)}
        onChange={handleChange}
      /> :
        <div>{PayByLinehaulTypeList.find((x: IDName) => x.ID == dataItem.PayBy)?.Name}</div>
      }
    </td>
  );
};

const DateCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const handleChange = (e: Date) => {
    if (e && props.field === "StartDate") {
      e = dayjs.utc(e).tz("America/New_York").toDate();
    } else if (e) {
      e = dayjs.utc(e).tz("America/New_York").endOf('day').toDate();
    }

    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: null,
        value: e,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {props.dataItem.inEdit ?
        <SimpleDatePicker
          value={props.dataItem[props.field]}
          onChange={handleChange}
        />
        :
        <div className={props.field === 'EndDate' && props.dataItem.IsExpired ? 'text-warning' : ''}>
          {dayjs.utc(props.dataItem[props.field]).tz("America/New_York").format("MM/DD/YYYY")}
        </div>
      }
    </td>
  );
};

const PayRateLinehaul = (props: Props) => {
  const [loading, setLoading] = useState(true);
  const editField: string = "inEdit";
  const [CSIDataCopy, setCSIDataCopy] = useState<PayRateViewModel[]>([]);
  const [PayRates, setPayRates] = useState<PayRateViewModel[]>([]);
  const [hideExpiredPayRateStructures, setHideExpiredPayRateStructures] = useState(true);
  const { alert } = useAlert();

  const PayRateLinehaulIDCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: PayRateViewModel = props.dataItem;

    return <td {...props.tdProps}>
      <span className="ml-2">{dataItem.isNew ? '-' : dataItem.PayRateLinehaulID}</span>
    </td>
  };

  const EditCommandCell = (props: EditCommandCellProps) => {
    const inEdit = props.dataItem[props.editField];
    const dataItem: PayRateViewModel = props.dataItem;
    const isNewItem = dataItem.isNew;

    const disabled = dataItem.StartDate == null || dataItem.EndDate == null || isNullOrWhiteSpace(dataItem.Name)

    return inEdit ? (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={disabled}
          onClick={() =>
            isNewItem ? props.add(dataItem) : props.update(dataItem)
          }
        >
          {isNewItem ? "Save" : "Update"}
        </Button>
        <Button themeColor={"secondary"}
          onClick={() =>
            isNewItem ? props.discard() : props.cancel(dataItem)
          }
        >
          {isNewItem ? "Discard" : "Cancel"}
        </Button>
      </td>
    ) : (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={PayRates.some(x => x.inEdit === true)}
          onClick={() => props.edit(dataItem)}
        >
          Edit
        </Button>
      </td>
    );
  };

  const refresh = useCallback(() => {
    setLoading(true);

    fetchApi('/api/PayRate/Linehaul')
      .then((summary: PayRateSummary) => {
        setLoading(false);

        summary.PayRates.forEach(obj => {
          obj.StartDate = dayjs.utc(obj.StartDate).tz("America/New_York").toDate();
          obj.EndDate = dayjs.utc(obj.EndDate).tz("America/New_York").toDate();
        });

        setPayRates(summary.PayRates);
        setCSIDataCopy(summary.PayRates);
      })
      .catch(async err => {
        setLoading(false);
        await alert(err);
      });
  }, [alert]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enterEdit = (dataItem: PayRateViewModel) => {
    if (PayRates.some(x => x.inEdit === true))
      return;

    setPayRates(
      PayRates.map((item) =>
        item.PayRateLinehaulID === dataItem.PayRateLinehaulID ? { ...item, inEdit: true } : item
      )
    );
  };

  const updatePayRates = (dataItem: PayRateViewModel) => {
    setLoading(true);
    const data = { ...dataItem }
    fetchApi('/api/PayRate/Linehaul', data, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          await alert(`Error: ${response.ErrorMessage}`);
        }
        else
          refresh();
      })
      .catch(async e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) await alert('An error occurred while saving.');
      });
  }

  const add = (dataItem: PayRateViewModel) => {
    updatePayRates(dataItem);
  };

  const update = (dataItem: PayRateViewModel) => {
    updatePayRates(dataItem);
  };

  const discard = () => {
    const newData = [...PayRates];
    newData.splice(0, 1);
    setPayRates(newData);
  };

  const cancel = (dataItem: PayRateViewModel) => {
    const originalItem = CSIDataCopy.find(
      (p) => p.PayRateLinehaulID === dataItem.PayRateLinehaulID
    );
    const newData = PayRates.map((item) =>
      item.PayRateLinehaulID === originalItem.PayRateLinehaulID ? originalItem : item
    );

    setPayRates(newData);
  };

  const generateId = () =>
    PayRates.reduce((acc, current) => Math.max(acc, current.PayRateLinehaulID), 0) + 1;

  const addNew = () => {
    const newDataItem = {
      inEdit: true,
      PayRateLinehaulID: generateId(),
      isNew: true,
      Name: '',
      StartDate: dayjs.utc().tz("America/New_York").startOf('day').toDate(),
      EndDate: dayjs([2999, 11, 31]).utc().tz("America/New_York").endOf('day').toDate(),
      IsExpired: false,
      PayRate: 0,
      PayBy: PayByLinehaulType.PerMile,
    } as PayRateViewModel;
    setPayRates([newDataItem, ...PayRates]);
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const dataItem = event.dataItem as PayRateViewModel;
    const newData = PayRates.map((item) =>
      item.PayRateLinehaulID === dataItem.PayRateLinehaulID
        ? { ...item, [event.field || ""]: event.value }
        : item
    );

    setPayRates(newData);
  }

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <EditCommandCell {...props}
      edit={enterEdit}
      //remove={remove}
      add={add}
      discard={discard}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );

  const dataView = () => {
    return (<>
      <div className="container-fluid mt-4" style={{ position: "relative" }}>
        {loading && <CenterDivPanel>
          <Loader type="converging-spinner" />
        </CenterDivPanel>}
        <h4 className="text-left">Pay Rate Linehaul Maintance</h4>
        <Grid
          scrollable="none"
          data={hideExpiredPayRateStructures ? PayRates.filter(x => x.IsExpired === false) : PayRates}
          dataItemKey="PayRateLinehaulID"
          onItemChange={itemChange}
          edit={Object.fromEntries(PayRates.filter(x => x.inEdit).map(x => [x.PayRateLinehaulID, true]))}
          onRowDoubleClick={(e: GridRowDoubleClickEvent) => { enterEdit(e.dataItem); }}
        >
          <GridToolbar>
            <Button themeColor={"primary"}
              disabled={PayRates.some(x => x.inEdit === true)}
              title="Add new"
              onClick={addNew}
            >
              Add New Pay Rate
            </Button>
            <span>
              <Checkbox
                disabled={PayRates.some(x => x.inEdit === true)}
                className="ml-2"
                value={hideExpiredPayRateStructures}
                onChange={(e) => setHideExpiredPayRateStructures(e.value)}
                label={"Hide Expired Pay Rate Structures"}
              />
            </span>
          </GridToolbar>
          <Column field="PayRateLinehaulID" title="Rate Code" editable={false} cells={{ data: PayRateLinehaulIDCell }} />
          <Column field="Name" title="Name" cells={{ data: textBoxCell }} />
          <Column field="PayBy" title="Pay By" cells={{ data: PayByCell }} />
          <Column field="PayRate" title="Rate" cells={{ data: RateCell, headerCell: RightAlignHeaderCell }} />
          <Column field="StartDate" title="Effective From" cells={{ data: DateCell }} width={175} />
          <Column field="EndDate" title="Effective To" cells={{ data: DateCell }} width={175} />
          <Column field="ModifiedByFullName" title="Modified By" cells={{ data: LastModifiedByCell }} width={250} />
          <Column cells={{ data: MyEditCommandCell }} width={160} />
        </Grid>
      </div>
    </>);
  };

  if (props.CloseDialog) {
    return <Dialog title={`Set Pay Rate Linehaul`} onClose={props.CloseDialog} className='dialog-w11/12'>
      {dataView()}
    </Dialog>
  }

  return dataView();
}

export default PayRateLinehaul;
